/**
 * LocationHistoryTimeline — mini stacked timeline showing every van's
 * movement between named branches over the last 24 h.
 *
 * Each van gets one horizontal bar; the bar is divided into coloured
 * segments where each colour maps to a distinct branch (or "In transit").
 * Hovering a segment reveals branch name + enter/leave time + duration.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Route } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const IN_TRANSIT = 'In transit';

// A stable palette; branches are colour-mapped by insertion order so the
// legend matches the timeline row-for-row across renders.
const PALETTE = [
  '#14b8a6', '#3b82f6', '#a855f7', '#f59e0b',
  '#ef4444', '#10b981', '#ec4899', '#06b6d4',
  '#eab308', '#f97316',
];

export default function LocationHistoryTimeline({ hours = 24 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/v1/ops/reports/location-history?hours=${hours}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ vans: [], branches: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hours]);

  const colorFor = useMemo(() => {
    if (!data) return () => '#94a3b8';
    const map = {};
    (data.branches || []).forEach((name, i) => {
      map[name] = name === IN_TRANSIT ? '#475569' : PALETTE[i % PALETTE.length];
    });
    return (name) => map[name] || '#94a3b8';
  }, [data]);

  const windowMs = useMemo(() => {
    if (!data) return 1;
    return new Date(data.window_to) - new Date(data.window_from);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading movement history…
      </div>
    );
  }

  if (!data || !data.vans?.length) {
    return (
      <div className="text-sm text-slate-500 py-6 text-center">
        No movement recorded in the last {hours} h.
      </div>
    );
  }

  const startMs = new Date(data.window_from).getTime();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Route className="w-4 h-4 text-teal-500" />
          Location History (last {hours} h)
        </h4>
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {new Date(data.window_from).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {'  →  '}
          {new Date(data.window_to).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* Timeline rows */}
      <div className="space-y-2">
        {data.vans.map(v => (
          <div key={v.device_uid} className="grid grid-cols-[140px_1fr] gap-3 items-center" data-testid={`loc-history-row-${v.device_uid}`}>
            <span className="font-mono text-xs text-slate-700 dark:text-slate-300 truncate" title={v.device_uid}>
              {v.device_uid}
            </span>
            <div className="relative h-6 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              {v.segments.map((seg, i) => {
                const enterMs = new Date(seg.enter_at).getTime();
                const leaveMs = new Date(seg.leave_at).getTime();
                const leftPct = Math.max(0, ((enterMs - startMs) / windowMs) * 100);
                const widthPct = Math.max(0.5, ((leaveMs - enterMs) / windowMs) * 100);
                return (
                  <div
                    key={`${v.device_uid}-${i}`}
                    className="absolute top-0 bottom-0 flex items-center justify-center text-[9px] text-white font-medium overflow-hidden hover:opacity-90 cursor-help"
                    style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: colorFor(seg.branch) }}
                    title={`${seg.branch}\n${new Date(seg.enter_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${new Date(seg.leave_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}\n${seg.duration_min} min`}
                  >
                    {widthPct > 8 && seg.duration_min >= 10 && (
                      <span className="truncate px-1">{seg.duration_min}m</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 pt-2 border-t border-slate-200 dark:border-slate-800">
        {(data.branches || []).map(name => (
          <span key={name} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400">
            <span
              className={cn("w-3 h-3 rounded-sm inline-block", name === IN_TRANSIT && "opacity-70")}
              style={{ backgroundColor: colorFor(name) }}
            />
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}
